import type { AuditEventsSessionView as AuditEventsView } from "@p0security/shared/integrations/sessions/types";
import {
  Button,
  Empty,
  Flex,
  Input,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";

import { JsonViewerModal } from "./JsonViewerModal";
import { SessionViewHeader } from "./SessionViewHeader";

const AuditEventActions = ({
  record,
  onExpandJson,
}: {
  record: { raw?: unknown; details?: unknown };
  onExpandJson: (data: unknown, title: string) => void;
}) => {
  const handleRawClick = useCallback(() => {
    onExpandJson(record.raw ?? record, "Raw Event");
  }, [onExpandJson, record]);

  const handleDetailsClick = useCallback(() => {
    onExpandJson(record.details, "Event Details");
  }, [onExpandJson, record.details]);

  return (
    <Flex gap="small">
      <Button type="link" onClick={handleRawClick} style={{ padding: 0 }}>
        raw
      </Button>
      {record.details != null ? (
        <Button type="link" onClick={handleDetailsClick} style={{ padding: 0 }}>
          details
        </Button>
      ) : null}
    </Flex>
  );
};

/** Renders audit event rows without duplicating the surrounding view header. */
export const AuditEventsRowsTable = ({
  rows,
}: {
  rows: AuditEventsView["rows"];
}) => {
  type AuditEventRow = AuditEventsView["rows"][number];
  const [search, setSearch] = useState("");
  const [quantity, setQuantity] = useState(10);

  const [expandedJson, setExpandedJson] = useState<{
    data: unknown;
    title: string;
  } | null>(null);

  const handleExpandJson = useCallback((data: unknown, title: string) => {
    setExpandedJson({ data, title });
  }, []);

  const handleCloseExpandedJson = useCallback(() => {
    setExpandedJson(null);
  }, []);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) =>
      [
        row.timestamp,
        row.severity,
        row.action,
        row.actor,
        row.target,
        row.summary,
      ]
        .filter((value): value is string => !!value)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    );
  }, [rows, search]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, quantity),
    [filteredRows, quantity]
  );

  if (rows.length === 0) {
    return (
      <Empty
        description="No audit events found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <Flex
        align="center"
        gap="small"
        justify="flex-end"
        style={{ marginBottom: 16 }}
      >
        <Input
          allowClear
          placeholder="Search audit events"
          size="small"
          style={{ width: 320 }}
          value={search}
          onChange={handleSearchChange}
        />
        <Typography.Text type="secondary">Showing latest</Typography.Text>
        <Select
          value={quantity}
          onChange={setQuantity}
          style={{ width: 64 }}
          size="small"
        >
          <Select.Option value={10}>10</Select.Option>
          <Select.Option value={20}>20</Select.Option>
          <Select.Option value={50}>50</Select.Option>
          <Select.Option value={100}>100</Select.Option>
        </Select>
      </Flex>
      {visibleRows.length === 0 ? (
        <Empty
          description="No audit events match the current filter"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          dataSource={visibleRows}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: "Event",
              key: "event",
              width: 150,
              render: (_value: unknown, record: AuditEventRow) => (
                <AuditEventActions
                  record={record}
                  onExpandJson={handleExpandJson}
                />
              ),
            },
            {
              title: "Time",
              dataIndex: "timestamp",
              key: "timestamp",
              width: 220,
              render: (value: string) => (
                <Typography.Text code>{value}</Typography.Text>
              ),
            },
            {
              title: "Severity",
              dataIndex: "severity",
              key: "severity",
              width: 120,
              render: (value?: "error" | "info" | "warning") => {
                if (!value) {
                  return null;
                }

                const color =
                  value === "error"
                    ? "error"
                    : value === "warning"
                      ? "warning"
                      : "default";

                return <Tag color={color}>{value}</Tag>;
              },
            },
            {
              title: "Action",
              dataIndex: "action",
              key: "action",
              width: 160,
              render: (value?: string) => value ?? null,
            },
            {
              title: "Actor",
              dataIndex: "actor",
              key: "actor",
              width: 180,
              render: (value?: string) => value ?? null,
            },
            {
              title: "Target",
              dataIndex: "target",
              key: "target",
              width: 180,
              render: (value?: string) => value ?? null,
            },
            {
              title: "Summary",
              dataIndex: "summary",
              key: "summary",
              render: (value: string) => (
                <Typography.Text style={{ whiteSpace: "pre-wrap" }}>
                  {value}
                </Typography.Text>
              ),
            },
          ]}
        />
      )}
      <JsonViewerModal
        isOpen={!!expandedJson}
        onClose={handleCloseExpandedJson}
        data={expandedJson?.data}
        title={expandedJson?.title || ""}
      />
    </>
  );
};

/** Renders normalized audit event session evidence such as Postgres or SaaS logs. */
export const AuditEventsSessionView = ({ view }: { view: AuditEventsView }) => {
  return (
    <>
      <SessionViewHeader view={view} fallbackTitle="Session Audit" />
      <AuditEventsRowsTable rows={view.rows} />
    </>
  );
};
